import { FestivalDateField } from 'app/components/festival/festival-date-field';
import { storage, StorageKey } from 'app/lib/storage';
import { isWhiteLabel, isNotWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { Column } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { MapAndRadius } from 'app/atoms/map-sheet';
import { Map } from 'app/design/primitives/map';
import { CountrySelect } from 'app/design/primitives/select/country';
import type { ContactInformationFlowApi } from 'app/screens/onboarding/hooks/use-contact-information-flow';
import type { ContactInformationFormValues } from 'app/screens/onboarding/hooks/use-contact-information-form';
import type { TFunction } from 'i18next';
import { Controller, type Control } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export type ContactInformationFlowContent = Omit<ContactInformationFlowApi, 'onBackPress' | 'step'>;

interface ContactInformationStepsProps {
  control: Control<ContactInformationFormValues>;
  deviceHeight: number;
  flow: ContactInformationFlowContent;
  t: TFunction;
  tAPI: TFunction;
  tSuffix: string;
  variant: 'highlight' | 'neutral';
}

const PostalStepPrompt = ({
  isHighlight,
  t,
  tSuffix,
}: Pick<ContactInformationStepsProps, 't' | 'tSuffix'> & { isHighlight: boolean }) => (
  <Text
    color={isHighlight ? 'text.primary' : 'icon.tertiary'}
    fontSize={15}
    lineHeight={22}
    marginBottom="spacing.general.x4"
    textAlign="center"
    variant="bodyBase"
  >
    {t(`prompt${tSuffix}`)}
  </Text>
);

const PostalStepForm = ({
  control,
  flow,
  t,
  tAPI,
}: Pick<ContactInformationStepsProps, 'control' | 'flow' | 't' | 'tAPI'>) => (
  <Column gap="spacing.general.x2">
    {isNotWhiteLabel() ? (
      <>
        <View style={{ height: theme.spacing['spacing.general.x6'] }} />
        <Controller
          {...{ control }}
          name="country"
          disabled={flow.isGeoCodeAddressFetching}
          render={({ field, fieldState }) => (
            <CountrySelect
              backgroundColor={theme.colors['surface.default']}
              error={fieldState.error}
              label={t('countryFieldLabel', { ns: 'my-address' })}
              onChange={field.onChange}
              textAlign="center"
              value={field.value}
            />
          )}
        />
      </>
    ) : null}

    <Controller
      {...{ control }}
      name="postalCode"
      render={({ field, fieldState }) => (
        <Input
          autoCapitalize="characters"
          autoComplete="postal-code"
          backgroundColor={theme.colors['surface.default']}
          editable={!flow.isGeoCodeAddressFetching}
          error={fieldState.error}
          label={t('postalCodeFieldLabel', { ns: 'my-address' })}
          labelColor="text.primary"
          onBlur={field.onBlur}
          onChangeText={(text) => field.onChange(text.toUpperCase())}
          testID="onboarding-postal-code-input"
          textAlign="center"
          textContentType="postalCode"
          value={field.value}
        />
      )}
    />

    {flow.geoCodeAddressError ? (
      <RemoteError {...{ error: flow.geoCodeAddressError, message: tAPI(flow.geoCodeAddressError.message) }} />
    ) : null}
  </Column>
);

const MapRadiusStep = ({
  flow,
  isHighlight,
  t,
}: Pick<ContactInformationStepsProps, 'flow' | 't'> & { isHighlight: boolean }) => (
  <Column gap="spacing.general.x4">
    <Text
      color="text.primary"
      fontSize={15}
      lineHeight={22}
      marginBottom="spacing.general.x2"
      textAlign="center"
      variant="bodyBase"
    >
      {t('mapStatsPrompt')}
    </Text>
    <MapAndRadius
      initialRadius={flow.radius}
      initialRadiusCenter={{
        latitude: flow.radiusCenter!.latitude,
        longitude: flow.radiusCenter!.longitude,
      }}
      markerPosition={{
        latitude: flow.addressCenter!.latitude,
        longitude: flow.addressCenter!.longitude,
      }}
      textColor={isHighlight ? theme.colors['text.primary'] : undefined}
      onChange={flow.onMapRadiusChange}
      variant={{
        audience: { type: 'onboarding-listings' },
        units: flow.country === 'US' ? 'miles' : 'automatic',
      }}
    />
  </Column>
);

const PinMapStep = ({
  deviceHeight,
  flow,
  isHighlight,
  t,
}: Pick<ContactInformationStepsProps, 'deviceHeight' | 'flow' | 't'> & { isHighlight: boolean }) => (
  <Column gap="spacing.general.x4">
    <Map
      height={deviceHeight * 0.36}
      {...(Platform.OS === 'ios' && { mapType: 'mutedStandard' })}
      region={{
        latitude: flow.addressCenter!.latitude,
        longitude: flow.addressCenter!.longitude,
        latitudeDelta: 0.03,
        longitudeDelta: 0.03,
      }}
      loadingBackgroundColor={theme.colors['surface.disabled']}
      onRegionChange={flow.debouncedSetAddressCenter}
      rotateEnabled={false}
      scrollDuringRotateOrZoomEnabled={false}
      zoomEnabled={false}
    >
      {flow.addressCenter ? <Map.Marker coordinate={flow.addressCenter} /> : null}
    </Map>
    <Text
      color={isHighlight ? 'text.primary' : undefined}
      fontSize={15}
      lineHeight={22}
      textAlign="center"
      variant="bodyBase"
    >
      {t('mapMarkerPrompt')}
    </Text>
  </Column>
);

export function ContactInformationSteps({
  control,
  deviceHeight,
  flow,
  t,
  tAPI,
  tSuffix,
  variant,
}: ContactInformationStepsProps) {
  const isHighlight = variant === 'highlight';

  return (
    <>
      {flow.showPostalForm && isWhiteLabel(WhiteLabel.Refrain) ? (
        <Controller
          control={control}
          name="festivalStartDate"
          render={({ field, fieldState }) => (
            <FestivalDateField
              value={field.value}
              onChange={(date) => {
                field.onChange(date);
                void storage.saveString(StorageKey.TemporaryFestivalStartDate, date);
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      ) : null}
      {flow.showPostalForm ? (
        <Column gap="spacing.general.x2">
          {isWhiteLabel(WhiteLabel.Refrain) ? (
            <Text variant="bodyBaseMedium">{t('address', { ns: 'festival' })}</Text>
          ) : null}
          <Column
            backgroundColor="surface.primary"
            borderRadius="radius.lg"
            padding={isWhiteLabel(WhiteLabel.Refrain) ? 'spacing.general.x4' : 'spacing.general.0'}
          >
            <PostalStepPrompt {...{ isHighlight, t, tSuffix }} />
            <PostalStepForm {...{ control, flow, t, tAPI }} />
          </Column>
        </Column>
      ) : null}
      {flow.showMapRadius ? <MapRadiusStep {...{ flow, isHighlight, t }} /> : null}
      {flow.showPinMap ? <PinMapStep {...{ deviceHeight, flow, isHighlight, t }} /> : null}
    </>
  );
}
